import {autorun, observable} from "mobx";
import {ApiLayerConfiguration} from "../config/api-configuration";
import EsriFeatureLayer from "@arcgis/core/layers/FeatureLayer";
import EditsResult = __esri.EditsResult;
import {isNumber} from "lodash";
import Graphic from "@arcgis/core/Graphic";
import {Difference} from "../models/difference";
import {ItemType} from "../models/item-type";

export interface IStore {

    loadItem(id?: string): Promise<ItemType | null>;
    loadItems(ids?: string[]): Promise<ItemType[]>;
    addItem(item: ItemType | Map<string, ItemType>): void;
    removeItem(item: ItemType): void;
    getItem(guid: string): ItemType | null;
    get selectedItem(): ItemType | null;
    setSelectedItem(item: ItemType): void;
    resetSelectedItem(): void;

    getField(fieldName: string): __esri.Field | null;
    loadDomain(fieldName: string): Promise<__esri.CodedValueDomain | null>;

    saveFeature(feature: __esri.Graphic, mode: "add" | "update", user: string): Promise<number | null>;
    saveFeatures(layer: __esri.FeatureLayer,
        user: string,
        toAdd?: __esri.Graphic[],
        toUpdate?: __esri.Graphic[],
        toDelete?: __esri.Graphic[] | number[]): Promise<__esri.EditsResult>;
    deleteFeature(feature: __esri.Graphic): Promise<number | null>;

    get isLoading(): boolean;
    get isLoaded(): boolean;
    get isError(): boolean;

    loadApi(): Promise<void>;
}

export interface AbstractStoreProps {
    layerConfig: ApiLayerConfiguration;
}

export abstract class AbstractStore implements IStore {

    /**
     * The layer configuration
     */
    layerConfig: ApiLayerConfiguration;

    /**
     * Loaded items (local store)
     */
    items = observable.map<string, ItemType>();

    /**
     * The currently selected item of this store
     */
    selectedItem: ItemType | null;

    /**
     * The layer this API is connected to
     */
    layer: EsriFeatureLayer;

    /**
     * Domains stored by domain name
     */
    domains = observable.map<string, __esri.CodedValueDomain>();
    /**
     * Domains stored by field name
     */
    domainsByField =  observable.map<string, __esri.CodedValueDomain>();

    /**
     * API layer status
     */
    status: "loading" | "successful" | "error" | "not-found";

    /**
     * Data status
     */
    dataStatus: "loading" | "loaded" | "error";

    /**
     * Search terms and filters
     */
    searchTerm: string | undefined;

    /**
     * Label describing the class type
     */
    typeLabel: string;

    /**
     * Filters
     */
    filters: string[] = [];

    /**
     * Sorting
     */
    sort: string | undefined;

    protected constructor(props: AbstractStoreProps) {
        // do not use makeObservable here. Not supported for derived classes
        // use makeObservable in constructor in each derived class
        // make sure you include all properties and functions of this abstract base class!
        // !! add observable, action, computed in derived classes when new functionality is added to this class !!

        this.layerConfig = props.layerConfig;
    }

    get isDataLoading(): boolean {
        return this.dataStatus === "loading";
    }
    get isDataLoaded(): boolean {
        return this.dataStatus === "loaded";
    }
    get isDataError(): boolean {
        return this.dataStatus === "error";
    }
    get isLoading(): boolean {
        return this.status === "loading";
    }
    get isLoaded(): boolean {
        return this.status === "successful";
    }
    get isNotFound(): boolean {
        return this.status === "not-found";
    }
    get isError(): boolean {
        return this.status === "error";
    }

    /**
     * Add one or multiple items to the store
     * @param item One item or a map with multiple items with GUID as key
     */
    addItem(item: ItemType | Map<string, ItemType>): void {
        if (item instanceof Map) {
            // multiple items
            this.items.merge(item);
        }
        else {
            // one item
            if (this.items.has(item.guid)) {
                this.items.delete(item.guid);
            }
            this.items.set(item.guid, item);
        }
    }

    /**
     * Remove an item from store
     * @param item
     */
    removeItem(item: ItemType): void {
        if (this.items.has(item.guid)) {
            this.items.delete(item.guid);
        }
    }

    /**
     * Remove all items
     */
    clearItems(): void {
        this.items.clear();
    }

    clearFilters(): void {
        this.filters = [];
    }

    /**
     * Get an item by GUID
     * @param guid
     */
    getItem(guid: string): ItemType | null {
        const item = this.items.get(guid);
        return item || null;
    }

    setSelectedItem(item: ItemType): void {
        this.selectedItem = item;
    }

    resetSelectedItem(): void {
        this.selectedItem = null;
    }

    getField(fieldName: string): __esri.Field | null {
        if (!this.isLoaded) {
            throw new Error(`Layer not ready requesting ${fieldName}`);
        }
        return this.layer.getField(fieldName) as __esri.Field;
    }

    /**
     * Get field alias name for an object property
     * @param dbFieldName
     */
    getFieldAlias(dbFieldName: string): string | undefined {
        if (!dbFieldName) {
            throw new Error(`No database field found for ${dbFieldName}`);
        }

        if (!this.isLoaded) {
            // API should be loaded when fields are requested
            throw new Error("API not loaded.");
        }

        const field = this.getField(dbFieldName);
        return field?.alias;
    }

    /**
     * Load coded value domain by field name
     *
     * @param dbFieldName The name of the field the domain is related to (database field name)
     */
    loadDomain(dbFieldName: string): Promise<__esri.CodedValueDomain | null> {
        return new Promise((resolve, reject) => {
            // wait for API to load
            autorun((reaction: any) => {
                if (!this.isLoaded || !this.layer) {
                    return;
                }
                reaction.dispose(); // stop autorun

                if (!dbFieldName) {
                    reject("No database field name provided.");
                }

                const domain = this.layer.getFieldDomain(dbFieldName) as __esri.CodedValueDomain;
                if (domain) {
                    this.addDomain(dbFieldName, domain);
                    resolve(domain);
                } else {
                    console.warn(`Domain not found for field ${dbFieldName}`);
                    resolve(null);
                }
            });
        });
    }

    /**
     * Create a new feature of the specified type
     * Uses layers FeatureTemplate prototype
     *
     * @see: https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-support-FeatureTemplate.html#prototype
     * @param geometry
     * @param props
     */
    createFeature(geometry?: __esri.Geometry | null, props?: ItemType): __esri.Graphic {
        if (!this.isLoaded) {
            throw new Error("Layer not found creating feature.");
        }
        const template = this.layer.templates[0] as __esri.FeatureTemplate;
        const feature = new Graphic({
            attributes: {...template.prototype.attributes},  // do clone! otherwise attributes are referenced and reused
            layer: this.layer // store reference to layer. is needed when editing this feature in editor
        });

        if (geometry) {
            feature.geometry = geometry.clone();
        }
        if (props) {
            // Loop through updated attributes and assign
            // the updated values to feature attributes.
            const json: any = props.toJSON();
            if (!json) {
                throw new Error("Error converting object to JSON creating feature.");
            }
            Object.keys(json).forEach((name) => {
                feature.attributes[name] = json[name];
            });
        }

        return feature;
    }

    /**
     * Save feature to layer
     *
     * @param feature
     * @param mode
     * @param user
     */
    // eslint-disable-next-line max-len
    saveFeature(feature: __esri.Graphic, mode: "add" | "update", user: string): Promise<any> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose();

                const toAdd: __esri.Graphic[] = [];
                const toUpdate: __esri.Graphic[] = [];

                if (mode === "update") {
                    if (!feature.attributes.OBJECTID) {
                        throw new Error("ObjectId must be defined when updating feature.");
                    }
                    feature.attributes.editor = user;
                    toUpdate.push(feature);
                } else {
                    // new feature --> add
                    feature.attributes.creator = user;
                    toAdd.push(feature);
                }

                return this.saveFeatures(this.layer, user, toAdd, toUpdate)
                    .then((editsResult: EditsResult) => {
                        // Get the objectId of the newly added feature.
                        // Call selectFeature function to highlight the new feature.
                        if (editsResult.updateFeatureResults.length > 0) {
                            // updated
                            const objectId = editsResult.updateFeatureResults[0].objectId;
                            if (!objectId && editsResult.updateFeatureResults[0].error) {
                                console.error("Error updating feature in applyEdits.", editsResult.updateFeatureResults[0].error);
                                throw new Error("Error updating feature in applyEdits.");
                            }
                            resolve(objectId);
                            return;
                        } else if (editsResult.addFeatureResults.length > 0) {
                            // added
                            const objectId = editsResult.addFeatureResults[0].objectId;
                            if (!objectId && editsResult.addFeatureResults[0].error) {
                                console.error("Error adding feature in applyEdits.", editsResult.addFeatureResults[0].error);
                                throw new Error("Error adding feature in applyEdits.");
                            }
                            resolve(objectId);
                            return;
                        }
                        reject("No feature added or updated.");
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        });
    }

    saveFeatures(layer: __esri.FeatureLayer,
                 user: string,
                 toAdd?: __esri.Graphic[],
                 toUpdate?: __esri.Graphic[],
                 toDelete?: __esri.Graphic[] | number[]): Promise<__esri.EditsResult> {

        // check OBJECTIDs (needed for updates and deletes
        if (toUpdate?.some((f) => !f.attributes.OBJECTID) ||
            toDelete?.some((f) => !isNumber(f) && !f.attributes.OBJECTID)) {
            throw new Error("OBJECTID must be defined when updating or deleting feature.");
        }

        const edits = {
            addFeatures: toAdd,
            updateFeatures: toUpdate,
            deleteFeatures: toDelete
        };

        // send edits to the layer
        return layer.applyEdits(edits);
    };

    deleteFeature(feature: __esri.Graphic): Promise<any> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose();

                // Set up the applyEdits parameter with deletes.
                const edits = {
                    deleteFeatures: [feature]
                };

                // send edits to the layer
                this.layer.applyEdits(edits)
                    .then((editsResult: EditsResult) => {
                        // Get the objectId of the removed feature.
                        // Call selectFeature function to highlight the new feature.
                        if (editsResult.deleteFeatureResults.length > 0) {
                            // deleted
                            const objectId = editsResult.deleteFeatureResults[0].objectId;
                            if (!objectId && editsResult.deleteFeatureResults[0].error) {
                                console.error("Error deleting feature in applyEdits.",
                                    editsResult.deleteFeatureResults[0].error);
                                reject("Error deleting feature in applyEdits.");
                                throw new Error("Error deleting feature in applyEdits.");
                            }
                            resolve(objectId);
                            return;
                        }
                        reject("No feature deleted.");
                    })
                    .catch((error) => {
                        reject(error);
                        return;
                    });
            });
        });
    }

    loadApi(): Promise<void> {
        return new Promise((resolve, reject) => {

            this.status = "loading";

            this.loadLayer(this.layerConfig).then(() => {
                this.status = "successful";
                resolve();
            }, (error) => {
                console.error(error, this.layerConfig?.objectType);
                this.status = "error";
                reject(`Error loading layer. ${this.layerConfig.objectType} | ${this.layerConfig.portalItem.id} | ${this.layerConfig.layerId}`);
            });
        });
    }

    setDataStatus(status: "loading" | "loaded" | "error"): void {
        this.dataStatus = status;
    }

    /**
     * Request item from server and store in local store
     * @param id (Optional) ID of item to load
     */
    abstract loadItem(id: string | undefined): Promise<ItemType | null>;
    /**
     * Request items from server and store in local store
     * @param ids (Optional) IDs of items to load
     */
    abstract loadItems(ids?: string[]): Promise<ItemType[]>;

    /**
     * Query features from layer
     * @param ids
     * @param unique
     */
    abstract queryFeatures(ids?: string[], unique?: true): Promise<ItemType[] | [] | null>;

    /**
     * Zoom to feature of this item
     */
    abstract zoomToItem(guid: string): void;

    protected compareAttributes(before: any, after: any, comparableAttributes: string[]): Difference[] {
        const diff: Difference[] = [];
        comparableAttributes.forEach((attr: string) => {
            if (before[attr] !== after[attr]) {
                diff.push(new Difference({
                    o: before[attr],
                    n: after[attr],
                    p: attr
                }));
            }
        });
        return diff;
    }

    protected loadLayer(layerConfig: ApiLayerConfiguration): Promise<EsriFeatureLayer> {
        return new Promise((resolve, reject) => {
            try {
                const featureLayer: EsriFeatureLayer =
                    new EsriFeatureLayer(layerConfig as __esri.FeatureLayerProperties);

                featureLayer.load().then(() => {
                    this.layer = featureLayer;
                    resolve(featureLayer);
                }, () => {
                    reject(layerConfig.objectType);
                });
            } catch (errors) {
                console.error(errors);
                reject(layerConfig.objectType);
            }
        });
    }

    private addDomain(fieldName: string, domain: __esri.CodedValueDomain): void {
        if (this.domains.has(domain.name)) {
            this.domains.delete(domain.name);
        }
        this.domains.set(domain.name, domain);

        if (this.domainsByField.has(fieldName)) {
            this.domainsByField.delete(fieldName);
        }
        this.domainsByField.set(fieldName, domain);
    }

}
